// src/pages/UnitsPage.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { hasPropertyWriteAccess } from "../utils/roleAccess";
import Layout from "../components/layout/Layout";

import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Card,
  CardActionArea,
  CardContent,
  Alert,
  Skeleton,
} from "@mui/material";

import { getPropertyById, getUnitsByProperty } from "../api/unitApi";

function UnitsPage() {
  const { propertyId } = useParams();
  const { user, permissions } = useAuth();
  const navigate = useNavigate();

  const [property, setProperty] = useState(null);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const userRoles = user?.claims_role || user?.roles || [];
  const canEdit = hasPropertyWriteAccess(userRoles, permissions);

  useEffect(() => {
    if (!propertyId) return;

    const fetchData = async () => {
      try {
        const [propertyData, unitsData] = await Promise.all([
          getPropertyById(propertyId),
          getUnitsByProperty(propertyId),
        ]);

        setProperty(propertyData || null);
        setUnits(unitsData || []);
      } catch (err) {
        console.error("Error loading units:", err);
        setError("Failed to load units.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [propertyId]);

  const handleUnitClick = (unitId) => {
    if (!unitId) return console.warn("⚠️ Missing unitId");
    navigate(`/units/${unitId}/details`);
  };

  const handleEditClick = () => {
    navigate(`/properties/${propertyId}/edit`);
  };

  const trimTo = (value, maxLength = 30) => {
    if (!value) return "N/A";
    return value.length > maxLength ? value.slice(0, maxLength) : value;
  };

  const formatSqMeters = (squareFeet) => {
    if (!squareFeet) return "";
    const meters = Number(squareFeet) * 0.092903;
    if (Number.isNaN(meters)) return "";
    return ` (${meters.toFixed(2)} sqm)`;
  };

  const LoadingSkeleton = () => (
    <Box className="page-container">
      <Skeleton variant="text" width={280} height={38} />
      <Skeleton variant="text" width={360} />
      <Skeleton variant="rectangular" height={140} sx={{ mt: 3, mb: 2 }} />
      <Skeleton variant="rectangular" height={140} sx={{ mb: 2 }} />
    </Box>
  );

  if (loading) {
    return (
      <Layout>
        <LoadingSkeleton />
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Box className="page-container">
          <Alert severity="error">{error}</Alert>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box className="page-container">
        {/* HEADER / PROPERTY INFO */}
        {property && (
          <Paper
            elevation={2}
            sx={{ p: 3, borderRadius: 2, mb: 3, display: "flex", flexDirection: "column", gap: 1.5 }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="h5" fontWeight={600}>
                  Units for {property.name}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  {property.property_type || "Property"}
                </Typography>
              </Box>

              {canEdit && (
                <Button variant="contained" onClick={handleEditClick}>
                  Edit Property
                </Button>
              )}
            </Box>

            <Typography variant="body2" color="text.secondary">
              {property.address_line1}
              {property.address_line2 ? `, ${property.address_line2}` : ""},{" "}
              {property.city} {property.postcode}
            </Typography>
          </Paper>
        )}

        {/* UNITS GRID */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
            Units
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Click a unit card to view full unit details, utilities, assessments
            and insurance.
          </Typography>
        </Box>

        {units.length > 0 ? (
          <Grid container spacing={2}>
            {units.map((u) => {
              const id = u.id || u.unit_id;
              return (
                <Grid item xs={12} sm={6} md={4} key={id}>
                  <Card elevation={1} sx={{ borderRadius: 2 }}>
                    <CardActionArea onClick={() => handleUnitClick(id)}>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          Unit {u.unit_name || u.unit_code || "(No Name)"}
                        </Typography>
                        {u.unit_name && (
                          <Typography variant="body2" color="text.secondary">
                            {u.unit_name}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                          Floor: {u.floor || "N/A"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Size:{" "}
                          {u.square_feet
                            ? `${u.square_feet} sqft${formatSqMeters(u.square_feet)}`
                            : "Unknown"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Owner: {trimTo(u.owner_name)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Email: {trimTo(u.email)}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Alert severity="info">No units found for this property.</Alert>
        )}
      </Box>
    </Layout>
  );
}

export default UnitsPage;
